import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DocSheet from '../../../../../../../DocsArea/DocSheet';
import LibraryDocPane from '../../../../../../../DocsArea/LibraryDocPane';
import { DocEditorToolbar } from '../../../../../../../DocEditor/components/DocEditorToolbar';
import { useMissionDocSheetScroll } from '../hooks/useMissionDocSheetScroll';
import { DocScrollHost } from '../../../../../../../DocsArea/DocRestoreVeil';
import { DocSheetCloseButton } from '../../../../../../../DocsArea/DocSheetCloseButton';
import { missionDocLocation } from '../../../../../../../../utils/uniqueCampaignAncestor';

const TIP_OFFSET_X = 12;
const TIP_OFFSET_Y = 18;

/**
 * Full-bleed doc sheet opened over the mission modal, with back stack navigation.
 */
const MissionDocSheet = ({
  activeDocId,
  docStack,
  docsById,
  task,
  taskId,
  taskName,
  libraryDocs,
  libraryLoading,
  sheetCloseRef,
  handleBack,
  handleCloseSheet,
  missionDocDirect = false,
  handleOpenDoc,
  resolveDocMeta,
  onMissionDocTicksChange,
  onMissionDocFilesChange,
  onMissionVariablesChange,
  // Variable resolution context — the LIVE campaign this mission belongs to
  variableCampaign,
  allSprints,
  onCampaignVariablesChange,
  onPatchInheritedVariable,
}) => {
  const bodyRef = useRef(null);
  const nameTipElRef = useRef(null);
  const [nameTip, setNameTip] = useState(null);
  const holdCover = useMissionDocSheetScroll(activeDocId, bodyRef);

  const missionVariables = useMemo(
    () => (Array.isArray(task?.variableValues) ? task.variableValues : []),
    [task?.variableValues]
  );

  const location = useMemo(
    () =>
      missionDocLocation({
        campaignId: variableCampaign?.id,
        campaignName: variableCampaign?.name,
        missionName: taskName,
        sprints: allSprints,
      }),
    [variableCampaign?.id, variableCampaign?.name, taskName, allSprints]
  );

  useEffect(() => {
    if (!nameTip) nameTipElRef.current = null;
  }, [nameTip]);

  if (activeDocId === null) return null;

  const nestedBack = docStack.length > 1;
  const backLabel = nestedBack
    ? 'Back to doc'
    : missionDocDirect
      ? 'Back'
      : 'Back to mission';

  const placeNameTip = (clientX, clientY) => {
    const el = nameTipElRef.current;
    if (!el) return;
    el.style.left = `${clientX + TIP_OFFSET_X}px`;
    el.style.top = `${clientY + TIP_OFFSET_Y}px`;
  };

  const onNamePointerMove = (e) => {
    if (nameTip) {
      placeNameTip(e.clientX, e.clientY);
      return;
    }
    setNameTip({
      x: e.clientX + TIP_OFFSET_X,
      y: e.clientY + TIP_OFFSET_Y,
      text: location.tip,
    });
  };

  const onNamePointerLeave = () => setNameTip(null);

  const nameTipNode = nameTip
    ? createPortal(
        <div
          ref={nameTipElRef}
          className="mfv-doc-context__name-tip"
          style={{ left: nameTip.x, top: nameTip.y }}
          role="tooltip"
        >
          {nameTip.text}
        </div>,
        document.body
      )
    : null;

  return (
    <DocSheet onRequestClose={handleCloseSheet}>
      {(closeSheet) => {
        sheetCloseRef.current = closeSheet;
        return (
          <>
            {/* Context row: back left, centered note, close right */}
            <header className="mfv-doc-context">
              <button
                type="button"
                className="mfv-doc-context__back"
                onClick={nestedBack ? handleBack : closeSheet}
                title={`${backLabel} (Esc)`}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                <span className="mfv-doc-context__back-label">{backLabel}</span>
              </button>

              <p className="mfv-doc-context__note" role="note">
                <span className="mfv-doc-context__note-text">
                  <span className="mfv-doc-context__note-plain">
                    You&rsquo;re in{' '}
                  </span>
                  <strong
                    className="mfv-doc-context__note-name"
                    onPointerMove={onNamePointerMove}
                    onPointerLeave={onNamePointerLeave}
                  >
                    {location.displayName}
                  </strong>
                  <span className="mfv-doc-context__note-plain">
                    {' '}
                    Checkboxes and uploads here apply only to this mission.
                    A variable set to This document keeps its value on the doc.
                  </span>
                </span>
              </p>

              <DocSheetCloseButton onClose={closeSheet} />
            </header>

            {/* Toolbar chrome is surface-first: inert shell keeps Find/Code/
                Uncheck-all layout stable while the editor portals into the
                slot (or while the body is still a skeleton). */}
            <div className="doc-toolbar mfv-doc-toolbar">
              <div className="doc-editor-toolbar-shell" aria-hidden="true">
                <DocEditorToolbar
                  onFind={() => {}}
                  onToggleInlineCode={() => {}}
                  onUncheckAll={() => {}}
                  canUncheckAll={false}
                />
              </div>
              <span className="doc-editor-toolbar-slot" />
            </div>

            {/* Keep LibraryDocPane mounted across tab switches — remounting
                emptied the portaled toolbar and jerked the body. Doc id
                changes swap content under this stable chrome. */}
            <DocScrollHost className="doc-sheet__body-host" holdCover={holdCover}>
              <div className="doc-sheet__body" ref={bodyRef}>
                <div className="mfv-doc-sheet-page">
                  <LibraryDocPane
                    docId={activeDocId}
                    tickOverrides={task?.docTicks?.[activeDocId]}
                    onTicksChange={(overrides) =>
                      onMissionDocTicksChange?.(taskId, activeDocId, overrides)
                    }
                    fileOverrides={task?.docFiles?.[activeDocId]}
                    onFilesChange={(overrides) =>
                      onMissionDocFilesChange?.(taskId, activeDocId, overrides)
                    }
                    missionVariables={missionVariables}
                    onMissionVariablesChange={(rows) =>
                      onMissionVariablesChange?.(taskId, rows)
                    }
                    missionId={taskId}
                    onOpenDoc={handleOpenDoc}
                    resolveDocMeta={resolveDocMeta}
                    availableDocs={libraryLoading ? null : libraryDocs}
                    variableCampaign={variableCampaign}
                    allSprints={allSprints}
                    onCampaignVariablesChange={onCampaignVariablesChange}
                    onPatchInheritedVariable={onPatchInheritedVariable}
                    hideBodySkeleton={holdCover}
                  />
                </div>
              </div>
            </DocScrollHost>

            {nameTipNode}
          </>
        );
      }}
    </DocSheet>
  );
};

export default MissionDocSheet;
